import { Button, Result } from 'antd';
import { Navigate, useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from '@/lib/useAuth';
import { canUseShell, landingShell, shellPath, type Shell } from '@/lib/shells';

/**
 * Keeps a person inside a shell their roles fit.
 *
 * Like RequireAuth, this is a usability control rather than a security one.
 * Every request the page makes is authorised independently server-side and
 * masked before it leaves the API, so bypassing this in the browser yields an
 * empty screen and a run of 403s — not data.
 *
 * What it prevents is somebody being shown a product that is not theirs. An
 * employee looking for the check-in button should not first have to scroll past
 * an org chart editor.
 */
export function RequireShell({ shell, children }: { shell: Shell; children: ReactNode }) {
  const { identity } = useAuth();
  const navigate = useNavigate();

  // RequireAuth has already settled the session by the time this renders.
  const roles = identity?.roles ?? [];

  if (canUseShell(roles, shell)) {
    return <>{children}</>;
  }

  // Somebody with no roles at all is a provisioning accident, not a wrong turn,
  // and sending them to another shell would just loop.
  if (roles.length === 0) {
    return (
      <Result
        status="warning"
        title="Your account has no roles"
        subTitle="Ask an administrator to assign one. Until then there is nothing for you to see, which is the system failing safe rather than guessing."
      />
    );
  }

  const home = landingShell(roles);

  return (
    <Result
      status="403"
      title="Not your part of the product"
      subTitle="Your roles do not include this area. Nothing was exposed — the server would refuse these requests regardless."
      extra={
        <Button type="primary" onClick={() => void navigate(shellPath(home))}>
          Go to your home page
        </Button>
      }
    />
  );
}

/** Sends a signed-in user to the shell their roles imply. */
export function ShellRedirect() {
  const { identity } = useAuth();
  return <Navigate to={shellPath(landingShell(identity?.roles ?? []))} replace />;
}
